import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AmkLogo, { AmkMark } from '../components/AmkLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { api } from '../api';
import './LandingPage.css';

// Withdrawal window and trading days are defined in Sofia time
const FUND_TZ = 'Europe/Sofia';

interface Ticker {
  symbol: string;
  price: number;
  change24h: number;
}

// Fades sections in as they scroll into view
const useReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const targets = root.querySelectorAll('.lp-reveal');
    if (!('IntersectionObserver' in window)) {
      targets.forEach(el => el.classList.add('lp-in'));
      return;
    }
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('lp-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    targets.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
};

// Current weekday (1=Mon..7=Sun) and hour in the fund's timezone
const useFundClock = () => {
  const read = () => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: FUND_TZ, weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(new Date());
    const get = (type: string) => parts.find(p => p.type === type)?.value || '';
    const day = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(get('weekday')) + 1;
    return { day, hour: parseInt(get('hour'), 10) + parseInt(get('minute'), 10) / 60 };
  };
  const [clock, setClock] = useState(read);
  useEffect(() => {
    const id = setInterval(() => setClock(read()), 30000);
    return () => clearInterval(id);
  }, []);
  return clock;
};

const useMarket = () => {
  const [tickers, setTickers] = useState<Ticker[]>([]);
  useEffect(() => {
    let alive = true;
    const load = () => api.getPublicMarket().then(d => { if (alive && Array.isArray(d)) setTickers(d); }).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return tickers;
};

const formatPrice = (p: number) =>
  p >= 1000 ? p.toLocaleString('en-US', { maximumFractionDigits: 0 })
    : p >= 1 ? p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : p.toLocaleString('en-US', { maximumFractionDigits: 4 });

const Icon: React.FC<{ d: React.ReactNode; size?: number }> = ({ d, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);

const icons = {
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  trend: <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></>,
  eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,
  wallet: <><path d="M21 12V7H5a2 2 0 010-4h14v4" /><path d="M3 5v14a2 2 0 002 2h16v-5" /><path d="M18 12a2 2 0 100 4h4v-4h-4z" /></>,
  key: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></>,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" /></>,
  log: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></>,
  phone: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 3h-8" /></>,
  bell: <><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  globe: <><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></>,
};

const ArrowIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
);

// Deterministic pseudo heatmap for the P&L calendar preview
const HEAT = [2, 3, 1, 3, 2, 0, 0, 3, 2, 3, 3, 1, 0, 0, 1, 3, 2, 2, 3, 0, 0, 3, 3, 1, 2, 3, 0, 0];

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const rootRef = useReveal();
  const clock = useFundClock();
  const tickers = useMarket();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const year = new Date().getFullYear();

  const pillars = [
    { icon: icons.shield, key: 'p1' },
    { icon: icons.trend, key: 'p2' },
    { icon: icons.eye, key: 'p3' },
    { icon: icons.wallet, key: 'p4' },
  ];
  const steps = [1, 2, 3].map(i => ({ title: t(`landing.s${i}Title`), text: t(`landing.s${i}Text`) }));
  const features = [
    { icon: icons.trend, key: 'f1' },
    { icon: icons.calendar, key: 'f2' },
    { icon: icons.bell, key: 'f3' },
    { icon: icons.globe, key: 'f4' },
  ];
  const security = [
    { icon: icons.key, key: 'sec1' },
    { icon: icons.mail, key: 'sec2' },
    { icon: icons.wallet, key: 'sec3' },
    { icon: icons.log, key: 'sec4' },
  ];
  const faqs = [1, 2, 3, 4, 5, 6].map(i => ({ q: t(`landing.faq${i}Q`), a: t(`landing.faq${i}A`) }));

  // Weekly rhythm: Mon = trading + withdrawal window, Tue–Fri trading, Sat–Sun closed
  const days = [1, 2, 3, 4, 5, 6, 7].map(d => ({
    d,
    name: t(`landing.day${d}`),
    kind: d === 1 ? 'withdraw' : d <= 5 ? 'trade' : 'rest',
  }));
  const windowOpenNow = clock.day === 1 && clock.hour >= 10 && clock.hour < 17;

  return (
    <div className="lp" ref={rootRef}>
      <div className="lp-bg" aria-hidden="true">
        <div className="lp-bg-glow lp-bg-glow--1" />
        <div className="lp-bg-glow lp-bg-glow--2" />
        <div className="lp-bg-glow lp-bg-glow--3" />
        <div className="lp-bg-grain" />
      </div>

      {/* Nav */}
      <header className="lp-nav">
        <div className="lp-container lp-nav-inner">
          <Link to="/" className="lp-nav-logo"><AmkLogo size={34} /></Link>
          <nav className="lp-nav-links">
            <a href="#strategy">{t('landing.navStrategy')}</a>
            <a href="#platform">{t('landing.navPlatform')}</a>
            <a href="#rhythm">{t('landing.navRhythm')}</a>
            <a href="#security">{t('landing.navSecurity')}</a>
            <a href="#faq">{t('landing.navFaq')}</a>
          </nav>
          <div className="lp-nav-actions">
            <LanguageSwitcher />
            <Link to="/login" className="lp-btn lp-btn--ghost">{t('landing.signIn')}</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="lp-hero">
        <div className="lp-container lp-hero-inner">
          <div className="lp-hero-copy">
            <span className="lp-eyebrow lp-rise" style={{ animationDelay: '0.05s' }}>
              <span className="lp-eyebrow-dot" />{t('landing.eyebrow')}
            </span>
            <h1 className="lp-hero-title">
              <span className="lp-rise" style={{ animationDelay: '0.15s' }}>{t('landing.heroTitle1')}</span>
              <span className="lp-rise lp-gold-text" style={{ animationDelay: '0.3s' }}>{t('landing.heroTitle2')}</span>
            </h1>
            <p className="lp-hero-text lp-rise" style={{ animationDelay: '0.45s' }}>{t('landing.heroText')}</p>
            <div className="lp-hero-cta lp-rise" style={{ animationDelay: '0.6s' }}>
              <Link to="/login" className="lp-btn lp-btn--gold">
                {t('landing.investorSignIn')}
                <ArrowIcon />
              </Link>
              <a href="#platform" className="lp-btn lp-btn--text">{t('landing.explore')}</a>
            </div>
            <ul className="lp-hero-facts lp-rise" style={{ animationDelay: '0.75s' }}>
              <li><strong>{t('landing.fact1Value')}</strong><span>{t('landing.fact1Label')}</span></li>
              <li><strong>{t('landing.fact2Value')}</strong><span>{t('landing.fact2Label')}</span></li>
              <li><strong>{t('landing.fact3Value')}</strong><span>{t('landing.fact3Label')}</span></li>
            </ul>
          </div>

          <div className="lp-hero-visual lp-rise" style={{ animationDelay: '0.35s' }} aria-hidden="true">
            <div className="lp-orbit lp-orbit--1" />
            <div className="lp-orbit lp-orbit--2" />
            <div className="lp-card">
              <div className="lp-card-head">
                <span className="lp-live"><span className="lp-live-dot" />{t('landing.liveLabel')}</span>
                <AmkMark size={28} />
              </div>
              <svg className="lp-chart" viewBox="0 0 400 200" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="lp-chart-stroke" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#8a6a24" />
                    <stop offset=".6" stopColor="#d4a237" />
                    <stop offset="1" stopColor="#f6d77a" />
                  </linearGradient>
                  <linearGradient id="lp-chart-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#d4a237" stopOpacity=".28" />
                    <stop offset="1" stopColor="#d4a237" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[40, 80, 120, 160].map(y => <line key={y} x1="0" x2="400" y1={y} y2={y} className="lp-chart-grid" />)}
                <path className="lp-chart-area" d="M0 170 L40 158 L80 162 L120 136 L160 142 L200 112 L240 118 L280 84 L320 92 L360 52 L400 30 L400 200 L0 200 Z" fill="url(#lp-chart-fill)" />
                <path className="lp-chart-line" d="M0 170 L40 158 L80 162 L120 136 L160 142 L200 112 L240 118 L280 84 L320 92 L360 52 L400 30" stroke="url(#lp-chart-stroke)" />
                <circle className="lp-chart-dot" cx="400" cy="30" r="5" />
              </svg>
              <div className="lp-card-bars">
                {[38, 54, 30, 66, 48, 72, 60].map((h, i) => (
                  <span key={i} style={{ height: `${h}%`, animationDelay: `${1.2 + i * 0.08}s` }} />
                ))}
              </div>
            </div>
            <div className="lp-float lp-float--1">
              <span className="lp-float-icon lp-float-icon--green"><Icon d={icons.trend} size={16} /></span>
              <div><strong>{t('landing.floatTradeTitle')}</strong><span>{t('landing.floatTradeText')}</span></div>
            </div>
            <div className="lp-float lp-float--2">
              <span className="lp-float-icon"><Icon d={icons.wallet} size={16} /></span>
              <div><strong>{t('landing.floatPayoutTitle')}</strong><span>TRC20 · USDT</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Live market ticker (real Bybit prices) */}
      <div className="lp-ticker" aria-label={t('landing.tickerLabel')}>
        <span className="lp-ticker-label"><span className="lp-live-dot" /><span className="lp-ticker-label-text">{t('landing.tickerLabel')}</span></span>
        <div className="lp-ticker-viewport">
          {tickers.length > 0 ? (
            <div className="lp-ticker-track">
              {[0, 1].map(n => (
                <React.Fragment key={n}>
                  {tickers.map(tk => (
                    <span key={`${n}-${tk.symbol}`} className="lp-ticker-item" aria-hidden={n === 1}>
                      <b>{tk.symbol}</b>
                      <span className="lp-ticker-price">${formatPrice(tk.price)}</span>
                      <span className={tk.change24h >= 0 ? 'lp-up' : 'lp-down'}>
                        {tk.change24h >= 0 ? '▲' : '▼'} {Math.abs(tk.change24h).toFixed(2)}%
                      </span>
                    </span>
                  ))}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className="lp-ticker-skeleton">{Array.from({ length: 6 }).map((_, i) => <span key={i} />)}</div>
          )}
        </div>
      </div>

      {/* Pillars */}
      <section className="lp-section">
        <div className="lp-container">
          <div className="lp-section-head lp-reveal">
            <span className="lp-eyebrow">{t('landing.pillarsEyebrow')}</span>
            <h2 className="lp-section-title">{t('landing.pillarsTitle')}</h2>
          </div>
          <div className="lp-pillars">
            {pillars.map((p, i) => (
              <article key={p.key} className="lp-pillar lp-reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
                <span className="lp-pillar-num">0{i + 1}</span>
                <div className="lp-pillar-icon"><Icon d={p.icon} /></div>
                <h3>{t(`landing.${p.key}Title`)}</h3>
                <p>{t(`landing.${p.key}Text`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Strategy */}
      <section className="lp-section lp-section--tint" id="strategy">
        <div className="lp-container lp-strategy">
          <div className="lp-strategy-copy lp-reveal">
            <span className="lp-eyebrow">{t('landing.strategyEyebrow')}</span>
            <h2 className="lp-section-title">{t('landing.strategyTitle')}</h2>
            <p className="lp-section-text">{t('landing.strategyText')}</p>

            <div className="lp-strategy-stats">
              <div>
                <strong>≤20<small>x</small></strong>
                <span>{t('landing.stratStat1')}</span>
              </div>
              <div>
                <strong>{t('landing.stratStat2Value')}</strong>
                <span>{t('landing.stratStat2')}</span>
              </div>
              <div>
                <strong>{t('landing.stratStat3Value')}</strong>
                <span>{t('landing.stratStat3')}</span>
              </div>
            </div>

            <div className="lp-leverage" aria-label={t('landing.leverageLabel')}>
              <div className="lp-leverage-head">
                <span>{t('landing.leverageLabel')}</span>
                <b>{t('landing.leverageCap')}</b>
              </div>
              <div className="lp-leverage-track">
                <div className="lp-leverage-fill" />
                <span className="lp-leverage-cap" />
              </div>
              <div className="lp-leverage-scale">
                <span>1x</span><span>5x</span><span>10x</span><span>15x</span><span>20x</span><span className="lp-leverage-off">50x+</span>
              </div>
            </div>
          </div>

          <div className="lp-dossier lp-reveal" aria-label={t('landing.dossierTitle')}>
            <div className="lp-dossier-head">
              <span className="lp-dossier-id">{t('landing.dossierTitle')} · AMK-01</span>
              <span className="lp-dossier-stamp">{t('landing.dossierStamp')}</span>
            </div>
            <dl className="lp-dossier-rows">
              <div><dt>{t('landing.dosStyle')}</dt><dd>{t('landing.dosStyleValue')}</dd></div>
              <div><dt>{t('landing.dosDirection')}</dt><dd>{t('landing.dosDirectionValue')}</dd></div>
              <div><dt>{t('landing.dosLeverage')}</dt><dd>{t('landing.dosLeverageValue')}</dd></div>
              <div><dt>{t('landing.dosUniverse')}</dt><dd><span className="lp-redact" style={{ width: '62%' }} /></dd></div>
              <div><dt>{t('landing.dosSignals')}</dt><dd><span className="lp-redact" style={{ width: '78%' }} /></dd></div>
              <div><dt>{t('landing.dosEdge')}</dt><dd><span className="lp-redact" style={{ width: '54%' }} /><span className="lp-redact lp-redact--short" style={{ width: '30%' }} /></dd></div>
              <div><dt>{t('landing.dosRisk')}</dt><dd><span className="lp-redact" style={{ width: '70%' }} /></dd></div>
            </dl>
            <p className="lp-dossier-note">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
              {t('landing.dossierNote')}
            </p>
          </div>
        </div>
      </section>

      {/* Platform preview */}
      <section className="lp-section" id="platform">
        <div className="lp-container lp-platform">
          <div className="lp-platform-copy lp-reveal">
            <span className="lp-eyebrow">{t('landing.platformEyebrow')}</span>
            <h2 className="lp-section-title">{t('landing.platformTitle')}</h2>
            <p className="lp-section-text">{t('landing.platformText')}</p>
            <ul className="lp-features">
              {features.map(f => (
                <li key={f.key}>
                  <span className="lp-feature-icon"><Icon d={f.icon} size={18} /></span>
                  <div>
                    <strong>{t(`landing.${f.key}Title`)}</strong>
                    <span>{t(`landing.${f.key}Text`)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lp-device lp-reveal" aria-hidden="true">
            <div className="lp-device-bar"><i /><i /><i /><span>amkventures.io</span></div>
            <div className="lp-device-body">
              <aside className="lp-device-side">
                <AmkMark size={22} />
                {[0, 1, 2, 3, 4].map(i => <span key={i} className={i === 0 ? 'on' : ''} />)}
              </aside>
              <div className="lp-device-main">
                <div className="lp-device-stats">
                  {['gold', 'green', 'gold', 'amber'].map((c, i) => (
                    <div key={i} className={`lp-device-stat lp-device-stat--${c}`}><span /><b /></div>
                  ))}
                </div>
                <div className="lp-device-chart">
                  <svg viewBox="0 0 300 90" preserveAspectRatio="none">
                    <path d="M0 80 C30 74 45 70 70 62 S110 64 135 50 S175 42 200 34 S250 22 300 8" fill="none" stroke="#d4a237" strokeWidth="2" />
                    <path d="M0 80 C30 74 45 70 70 62 S110 64 135 50 S175 42 200 34 S250 22 300 8 L300 90 L0 90 Z" fill="rgba(212,162,55,0.12)" />
                  </svg>
                </div>
                <div className="lp-device-row">
                  <div className="lp-device-heat">
                    {HEAT.map((h, i) => <span key={i} className={`h${h}`} />)}
                  </div>
                  <div className="lp-device-positions">
                    {['BTC', 'ETH', 'SOL'].map((s, i) => (
                      <div key={s}>
                        <b>{s}/USDT</b>
                        <em className={i === 2 ? 'short' : 'long'}>{i === 2 ? 'SHORT' : 'LONG'}</em>
                        <span className="lp-device-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly rhythm */}
      <section className="lp-section" id="rhythm">
        <div className="lp-container">
          <div className="lp-section-head lp-section-head--center lp-reveal">
            <span className="lp-eyebrow">{t('landing.rhythmEyebrow')}</span>
            <h2 className="lp-section-title">{t('landing.rhythmTitle')}</h2>
            <p className="lp-section-text">{t('landing.rhythmText')}</p>
          </div>
          <div className="lp-week lp-reveal">
            {days.map(day => (
              <div key={day.d} className={`lp-day lp-day--${day.kind} ${clock.day === day.d ? 'lp-day--today' : ''}`}>
                {clock.day === day.d && <span className="lp-day-now">{t('landing.today')}</span>}
                <span className="lp-day-name">{day.name}</span>
                <span className="lp-day-icon">
                  <Icon d={day.kind === 'withdraw' ? icons.wallet : day.kind === 'trade' ? icons.trend : icons.calendar} size={18} />
                </span>
                <span className="lp-day-kind">{t(`landing.kind_${day.kind}`)}</span>
                {day.kind === 'withdraw' && <span className="lp-day-time">10:00 – 17:00</span>}
              </div>
            ))}
          </div>
          <p className={`lp-week-status lp-reveal ${windowOpenNow ? 'is-open' : ''}`}>
            <span className="lp-live-dot" />
            {windowOpenNow ? t('landing.windowOpenNow') : t('landing.windowNext')}
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="lp-section lp-section--tint">
        <div className="lp-container lp-steps-inner">
          <div className="lp-section-head lp-reveal">
            <span className="lp-eyebrow">{t('landing.stepsEyebrow')}</span>
            <h2 className="lp-section-title">{t('landing.stepsTitle')}</h2>
          </div>
          <ol className="lp-steps">
            {steps.map((s, i) => (
              <li key={s.title} className="lp-step lp-reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                <span className="lp-step-num">{i + 1}</span>
                <div>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Security */}
      <section className="lp-section" id="security">
        <div className="lp-container">
          <div className="lp-section-head lp-reveal">
            <span className="lp-eyebrow">{t('landing.securityEyebrow')}</span>
            <h2 className="lp-section-title">{t('landing.securityTitle')}</h2>
          </div>
          <div className="lp-security">
            {security.map((s, i) => (
              <article key={s.key} className="lp-sec-card lp-reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
                <span className="lp-sec-icon"><Icon d={s.icon} /></span>
                <h3>{t(`landing.${s.key}Title`)}</h3>
                <p>{t(`landing.${s.key}Text`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section className="lp-section" id="faq">
        <div className="lp-container lp-faq-inner">
          <div className="lp-section-head lp-reveal">
            <span className="lp-eyebrow">{t('landing.faqEyebrow')}</span>
            <h2 className="lp-section-title">{t('landing.faqTitle')}</h2>
          </div>
          <div className="lp-faq lp-reveal">
            {faqs.map((f, i) => (
              <div key={i} className={`lp-faq-item ${openFaq === i ? 'is-open' : ''}`}>
                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span>{f.q}</span>
                  <i aria-hidden="true" />
                </button>
                <div className="lp-faq-answer"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="lp-section lp-section--slim">
        <div className="lp-container">
          <div className="lp-cta lp-reveal">
            <AmkMark size={56} className="lp-cta-mark" />
            <div className="lp-cta-copy">
              <h2>{t('landing.ctaTitle')}</h2>
              <p>{t('landing.ctaText')}</p>
            </div>
            <Link to="/login" className="lp-btn lp-btn--gold">
              {t('landing.signIn')}
              <ArrowIcon />
            </Link>
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <AmkLogo size={26} />
          <p className="lp-footer-risk">{t('landing.footerRisk')}</p>
          <p className="lp-footer-rights">{t('landing.footerRights', { year })}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
